import { apiClient } from './client';

/**
 * 근태 0단계 — **입사일과 연차** 관리 API (2026-08-23).
 *
 * 서버: `apps/chat-server/src/modules/hr/leave-admin.routes.ts` (ADMIN 전용).
 * 설계: `docs/attendance-round2-sample.html` 화면 C(입사일 일괄) · 화면 O(연차 관리) · 제안 6.
 *
 * `api/hr.ts` 는 본인의 휴가·출장 "신청", `api/attendance.ts` 는 본인의 출퇴근 "사실 기록" 이다.
 * 여기는 **남의 것을 다루는 관리 화면**이라 파일을 나눠 둔다 — 권한도 계약도 다르다.
 */

/** 부여 규칙 한 구간. 서버 `hr_leave_rules.tiers` 의 원소 그대로. */
export interface LeaveTierDto {
  months: number;
  perMonth?: number;
  max?: number;
  days?: number;
  everyMonths?: number;
  plus?: number;
}

export interface LeaveRuleDto {
  tiers: LeaveTierDto[];
  maxDays: number;
  /** `HIRE_DATE` | `FISCAL_YEAR`. */
  basis: string;
  firstYearBonus: number;
  carryOver: boolean;
  /**
   * 회계연도 시작월(1~12) — `basis = FISCAL_YEAR` 일 때만 뜻이 있다 (2026-08-30 · 판 J).
   * ★ 옛 서버는 이 칸을 안 준다 → 없으면 1월로 읽는다.
   */
  fiscalStartMonth?: number;
}

export interface LeaveRuleRowDto extends LeaveRuleDto {
  id: string;
  /** `YYYY-MM-DD`. */
  effectiveFrom: string;
  /** 지금 적용되는 규칙 한 줄. 나머지는 "보관" 이다. */
  isCurrent: boolean;
}

export interface HireDateEmployeeDto {
  userId: string;
  name: string;
  email: string;
  position: string | null;
  teamName: string | null;
  employeeNumber: string | null;
  /** `YYYY-MM-DD`. 미설정이면 null — **여기가 비어 있으면 연차가 시작되지 않는다**. */
  hireDate: string | null;
  tenureMonths: number | null;
  tenureLabel: string | null;
  annualDays: number | null;
}

export interface QuotaRowDto {
  userId: string;
  name: string;
  teamName: string | null;
  hireDate: string | null;
  /** 입사일 × 규칙. 사람이 못 고친다. */
  autoTotal: number | null;
  /** 사람이 넣은 차이. 사유가 필수다. */
  adjustTotal: number;
  adjustReason: string | null;
  /** 자동 + 조정. 잔여의 출발점. */
  annualTotal: number | null;
  usedDays: number;
  remainDays: number | null;
  /**
   * **넘겨 쓴 만큼**(일) — 안 넘겼으면 0, 부여를 모르면 `null` (2026-09-11 · A-1).
   * ★ `remainDays` 는 0 에서 멈춘다 — 「잔여 0일」과 「2일 초과」는 담당자에게 다른 말이다.
   * ★ 옛 서버는 이 칸을 안 준다(`undefined`).
   */
  overdrawnDays?: number | null;
  /**
   * 보상휴가 (2026-08-30 · 판 J) — **연차와 다른 통**이다.
   * ★ 섞으면 「연차를 다 썼는데 잔여가 남았다」 같은 화면이 된다. 옛 서버는 이 칸을 안 준다.
   */
  substituteTotal?: number;
  substituteUsed?: number;
  /**
   * 휴일대체(§57)로 **쌓인** 몫(분) — 2026-09-06 판 B 에서 더한 칸.
   *
   * ★★★ 이 칸이 없으면 담당자는 「대체휴가 0일」을 보는데 직원에게는 9시간이 있다 —
   *    그 9시간은 **원장**에서 온다. 옆의 `substituteTotal` 은 **담당자가 넣은 조정**이다.
   * ★ **분으로 온다.** 일로 바꾸려면 사람마다 소정근로를 알아야 하는데, 목록 한 화면에서
   *   N 번 물을 값이 아니다 — 「9시간」이 지어낸 「1.1일」보다 정확하다.
   */
  substituteCreditedMinutes?: number;
}

/** 화면 C — 직원 목록(입사일 없는 사람이 먼저 온다) + 지금 적용되는 규칙. */
export async function listHireDates(year?: number): Promise<{
  year: number; rule: LeaveRuleDto; employees: HireDateEmployeeDto[];
}> {
  const { data } = await apiClient.get('/hr/employees/hire-dates', { params: year ? { year } : undefined });
  return data;
}

/** 화면 C — 입사일 일괄 저장. 저장과 동시에 그 해 연차가 확정된다(조정은 보존). */
export async function saveHireDates(items: Array<{ userId: string; hireDate: string | null }>): Promise<{
  year: number; updated: number;
}> {
  const { data } = await apiClient.patch('/hr/employees/hire-dates', { items });
  return data;
}

/** 화면 O — 부여 규칙 전체(시행일자 내림차순). */
export async function listLeaveRules(): Promise<{ rules: LeaveRuleRowDto[]; fallback: LeaveRuleDto }> {
  const { data } = await apiClient.get('/hr/leave-rules');
  return data;
}

/** 화면 O — 새 규칙. **기존 규칙은 고치지 않는다** — 같은 시행일자면 409. */
export async function createLeaveRule(body: {
  effectiveFrom: string; tiers: LeaveTierDto[]; maxDays?: number;
  basis?: string; firstYearBonus?: number; carryOver?: boolean; fiscalStartMonth?: number;
}): Promise<{ rule: LeaveRuleRowDto }> {
  const { data } = await apiClient.post('/hr/leave-rules', body);
  return data;
}

/** 화면 O — 그 해 자동·조정·최종·사용·잔여. */
export async function listLeaveQuotas(year?: number): Promise<{
  year: number; rule: LeaveRuleDto; ruleEffectiveFrom: string | null; rows: QuotaRowDto[];
}> {
  const { data } = await apiClient.get('/hr/leave-quotas', { params: year ? { year } : undefined });
  return data;
}

/**
 * 화면 O — 조정 일괄 저장.
 *
 * 서버가 막는 것 둘: 사유 없는 조정(`REASON_REQUIRED`) · 법정 최소 아래로 내리는 조정
 * (`BELOW_STATUTORY_MINIMUM`). 후자는 결근·휴직 차감(`deduction: true`)일 때만 열린다.
 */
export async function saveLeaveQuotas(year: number, items: Array<{
  userId: string;
  /** ★ 안 보내면 **연차 조정을 안 건드린다** — 보상휴가만 고치는 줄이 있어서다. */
  adjustTotal?: number; adjustReason?: string; deduction?: boolean;
  /** 보상휴가 부여 총량. ⚠ 사유는 **표에 칸이 없어 감사에만** 남는다. */
  substituteTotal?: number; substituteReason?: string;
}>): Promise<{ year: number; updated: number }> {
  const { data } = await apiClient.patch('/hr/leave-quotas', { year, items });
  return data;
}

/** 화면 O — 전원 자동 재계산. 조정은 지워지지 않는다. */
export async function recalcLeaveQuotas(year: number): Promise<{ year: number; recalculated: number }> {
  const { data } = await apiClient.post('/hr/leave-quotas/recalc', { year });
  return data;
}

// ── 연차 사용촉진 (2026-08-30 · 판 J · 근기법 §61) ─────────────────────────────

/** 촉진 통보 한 건의 기록. 이 표가 곧 **면책 증빙**이라 화면은 이것을 그대로 보여 준다. */
export interface PromotionRecordDto {
  id: string;
  sentAt: string;
  /** ★ **통보 시점의** 미사용 일수. 지금 다시 계산한 값이 아니다. */
  unusedDays: number;
  channel: string;
  plannedDates: string[] | null;
  respondedAt: string | null;
  note: string | null;
}

export type PromotionStatus =
  | 'NOT_NEEDED' | 'BEFORE' | 'DUE_FIRST' | 'LATE_FIRST'
  | 'WAIT_RESPONSE' | 'DUE_SECOND' | 'LATE_SECOND' | 'DONE' | 'NO_PERIOD';

export interface PromotionRowDto {
  userId: string;
  name: string;
  teamName: string | null;
  hireDate: string | null;
  /** 회차가 없으면(입사 전·입사일 미상) 여기부터 전부 null 이다. */
  periodStart: string | null;
  periodEnd: string | null;
  underOneYear: boolean;
  annualTotal: number | null;
  usedDays: number;
  unusedDays: number | null;
  firstFrom: string | null;
  firstTo: string | null;
  secondBy: string | null;
  status: PromotionStatus;
  notice: PromotionRecordDto | null;
  designation: PromotionRecordDto | null;
}

export async function listLeavePromotions(year?: number): Promise<{
  year: number; basis: string; fiscalStartMonth: number; rows: PromotionRowDto[];
}> {
  const { data } = await apiClient.get('/hr/leave-promotions', { params: year ? { year } : undefined });
  return data;
}

/** 보낸다 = **알린다 + 남긴다**. 못 보낸 사람은 `skipped` 에 이유가 붙어 온다. */
export async function sendLeavePromotion(body: {
  year: number;
  stage: 'NOTICE' | 'DESIGNATION';
  userIds: string[];
  channel?: 'APP' | 'EMAIL' | 'PAPER';
  note?: string;
  plannedDates?: string[];
}): Promise<{
  year: number; stage: string; sent: number;
  written: Array<{ userId: string; name: string; unusedDays: number }>;
  skipped: Array<{ userId: string; name: string; reason: string }>;
}> {
  const { data } = await apiClient.post('/hr/leave-promotions', body);
  return data;
}

/** 근로자 회신(사용 예정일)을 적는다. */
export async function recordPromotionResponse(id: string, body: {
  plannedDates?: string[]; note?: string;
}): Promise<PromotionRecordDto> {
  const { data } = await apiClient.patch(`/hr/leave-promotions/${id}`, body);
  return data;
}

// ── 퇴사자 연차 정산 (2026-08-30 · 판 J) ──────────────────────────────────

export interface SettlementBasisDto {
  /** `COMPANY`(회사 규칙) · `STATUTORY`(입사일 기준 · 법정 견줌) */
  kind: string;
  periodStart: string;
  periodEnd: string;
  underOneYear: boolean;
  granted: number;
  used: number;
  unused: number;
}

export interface SettlementRowDto {
  offboardingId: string;
  userId: string;
  name: string;
  status: string;
  resignDate: string;
  resignKind: string;
  hireDate: string | null;
  dailyWorkMinutes: number;
  company: SettlementBasisDto | null;
  statutory: SettlementBasisDto | null;
  /** 두 기준 중 **유리한 쪽**. 회차가 없으면(입사일 미상) null. */
  settleBasis: string | null;
  settleDays: number | null;
  /** 연차와 **다른 통**이라 따로 온다. */
  substituteRemain: number;
  hourlyWage: number | null;
  /** ★ 임금 미등록이면 `null` — 0 이면 「줄 것이 없다」로 읽힌다. */
  estimatedPay: number | null;
}

export async function listLeaveSettlements(status?: 'OPEN' | 'DONE'): Promise<{ rows: SettlementRowDto[] }> {
  const { data } = await apiClient.get('/hr/leave-settlements', { params: status ? { status } : undefined });
  return data;
}
